import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Shield, ShieldAlert, ShieldCheck, RefreshCw, AlertTriangle, CheckCircle2, Zap, Loader2, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Production Webhook Hardening dashboard.
 *
 * Shows:
 *  - LIVE Stripe webhook endpoints + which event types they're configured for
 *  - Any required event types missing on a live endpoint (red banner)
 *  - One-click "Sync events" button that POSTs to /webhook-health/sync-events
 *    and updates the live endpoint to listen for the full required set
 *  - 7-day + 24h activity counters from payment_events
 *  - Duplicate raw_event_id detection (proves idempotency in production)
 *  - Reconciliation tally (failed/refunded/pending counts)
 */
const AdminWebhookHealthTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(null); // endpoint_id being synced
  const [syncResult, setSyncResult] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/webhook-health/diagnostics`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSync = async (endpointId) => {
    if (!window.confirm(`Update the LIVE Stripe webhook endpoint to listen for all required event types?\n\nThis modifies your production Stripe configuration. Existing event types stay intact — we only ADD what's missing.`)) return;
    setSyncing(endpointId); setSyncResult(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/webhook-health/sync-events`,
        { endpoint_id: endpointId }, { withCredentials: true });
      setSyncResult(r.data);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(null);
    }
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>;
  }

  const checks = data?.checks || {};
  const coverage = data?.stripe_endpoints?.coverage || [];
  const anyMissing = coverage.some(c => !c.all_required_present);

  return (
    <div className="space-y-6" data-testid="admin-webhook-health-tab">
      <div>
        <h2 className="font-heading text-lg font-bold text-[#0A0A0A] flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#BA1826]" />
          Production Webhook Hardening
        </h2>
        <p className="text-sm text-[#4B5563] mt-1">
          Verifies that your live Stripe webhook endpoint receives all events the backend reconciliation logic depends on. Read-only diagnostics plus one-click event-type sync.
        </p>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-xs text-[#9CA3AF]">As of {data?.as_of ? new Date(data.as_of).toLocaleString() : '—'}</p>
        <button onClick={refresh} disabled={loading}
          className="text-xs flex items-center gap-1 px-3 py-1.5 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA] disabled:opacity-50"
          data-testid="webhook-health-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* CHECKS SUMMARY */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="webhook-health-checks">
        {[
          { key: 'stripe_api_reachable', label: 'Stripe API' },
          { key: 'has_live_endpoint', label: 'Live Endpoint' },
          { key: 'all_endpoints_active', label: 'Endpoint Active' },
          { key: 'all_required_events_covered', label: 'Events Covered' },
          { key: 'idempotency_working', label: 'Idempotency' },
        ].map(c => (
          <div key={c.key} className={`p-3 rounded-lg border text-center ${
            checks[c.key] ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`} data-testid={`check-${c.key}`}>
            {checks[c.key] ? <CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-green-700" /> :
              <ShieldAlert className="w-5 h-5 mx-auto mb-1 text-red-700" />}
            <p className="text-xs font-semibold">{c.label}</p>
            <p className="text-[10px] mt-0.5 text-[#4B5563]">{checks[c.key] ? 'OK' : 'Attention'}</p>
          </div>
        ))}
      </div>

      {/* CRITICAL ALERT — MISSING EVENT COVERAGE */}
      {anyMissing && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-5" data-testid="webhook-missing-events-alert">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-7 h-7 text-red-700 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-heading text-base font-bold text-red-900">Live webhook endpoint is missing required events</h3>
              <p className="text-sm text-red-800 mt-1">
                Your reconciliation logic relies on the events listed below. Without them, delayed card declines, refunds, and async payments will <strong>not</strong> reverse revenue automatically.
              </p>
              {coverage.filter(c => !c.all_required_present).map(c => (
                <div key={c.endpoint_id} className="mt-3 bg-white border border-red-200 rounded p-3">
                  <p className="text-xs text-[#4B5563] font-mono break-all">{c.url}</p>
                  <p className="text-xs mt-2 font-semibold text-red-800">Missing events ({c.missing_events.length}):</p>
                  <ul className="text-xs font-mono space-y-0.5 mt-1">
                    {c.missing_events.map(e => <li key={e} className="text-red-700">— {e}</li>)}
                  </ul>
                  <button onClick={() => handleSync(c.endpoint_id)} disabled={syncing === c.endpoint_id}
                    className="mt-3 btn-primary text-sm disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid={`sync-events-btn-${c.endpoint_id}`}>
                    {syncing === c.endpoint_id ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Syncing…</> :
                      <><Zap className="w-3.5 h-3.5" /> One-click sync events to Stripe</>}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {syncResult && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-4" data-testid="webhook-sync-result">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-5 h-5 text-green-700" />
            <p className="font-bold text-green-900 text-sm">Webhook event types synced successfully</p>
          </div>
          <p className="text-xs text-green-800 mt-1">
            Added {syncResult.added_events?.length || 0} event types to <span className="font-mono break-all">{syncResult.url}</span>.
          </p>
          {syncResult.added_events?.length > 0 && (
            <ul className="text-xs font-mono mt-2 space-y-0.5">
              {syncResult.added_events.map(e => <li key={e} className="text-green-800">+ {e}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* LIVE ENDPOINTS DETAIL */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="live-endpoints-detail">
        <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-[#BA1826]" />Live Stripe webhook endpoints</h3>
        {data?.stripe_endpoints?.fetch_error ? (
          <p className="text-sm text-red-600">{data.stripe_endpoints.fetch_error}</p>
        ) : (data?.stripe_endpoints?.live_endpoints || []).length === 0 ? (
          <p className="text-sm text-[#4B5563]">No live webhook endpoints found in this Stripe account.</p>
        ) : (
          <div className="space-y-3">
            {data.stripe_endpoints.live_endpoints.map(e => {
              const cov = coverage.find(c => c.endpoint_id === e.id);
              const ok = cov?.all_required_present;
              return (
                <div key={e.id} className={`border rounded p-3 ${ok ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <a href={`https://dashboard.stripe.com/webhooks/${e.id}`} target="_blank" rel="noopener noreferrer"
                        className="text-xs font-mono text-[#BA1826] hover:underline break-all inline-flex items-center gap-1">
                        {e.url} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${e.status === 'enabled' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{e.status}</span>
                  </div>
                  <p className="text-[10px] text-[#4B5563]">API version: <span className="font-mono">{e.api_version}</span> · Enabled events: <strong>{e.enabled_events.length}</strong> {cov?.wildcard && '(wildcard *)'}</p>
                  {cov && !cov.all_required_present && (
                    <p className="text-[10px] text-red-700 mt-1">{cov.missing_events.length} required event(s) missing</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ACTIVITY + IDEMPOTENCY + RECONCILIATION */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="webhook-activity">
          <h3 className="font-heading font-bold text-sm mb-3">Activity (last 7 days)</h3>
          <p className="text-xs text-[#4B5563] mb-2">Total events recorded: <strong>{data?.activity_7d?.total_events || 0}</strong> ({data?.activity_24h?.total_events || 0} in last 24h)</p>
          {Object.keys(data?.activity_7d?.by_type || {}).length === 0 ? (
            <p className="text-xs text-[#9CA3AF]">No webhook events in the last 7 days.</p>
          ) : (
            <ul className="text-xs space-y-1">
              {Object.entries(data.activity_7d.by_type).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <li key={k} className="flex justify-between font-mono"><span>{k}</span><span className="font-bold">{v}</span></li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="webhook-idempotency">
          <h3 className="font-heading font-bold text-sm mb-3">Idempotency (last 7 days)</h3>
          <p className="text-xs text-[#4B5563] mb-2">Duplicate Stripe event IDs detected: <strong>{data?.idempotency?.duplicate_raw_event_ids_7d || 0}</strong></p>
          <p className="text-[11px] text-[#9CA3AF] italic mb-2">{data?.idempotency?.note}</p>
          {(data?.idempotency?.examples || []).slice(0, 5).map((d) => (
            <p key={d.raw_event_id} className="text-[10px] font-mono text-[#4B5563]">{d.raw_event_id} ({d.event_type}) × {d.count}</p>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="webhook-reconciliation">
        <h3 className="font-heading font-bold text-sm mb-3">Reconciliation (last 7 days)</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          {[
            ['Donations failed', data?.reconciliation_7d?.donations_failed, 'text-red-700'],
            ['Donations refunded', data?.reconciliation_7d?.donations_refunded, 'text-amber-700'],
            ['Donations pending (unresolved)', data?.reconciliation_7d?.donations_pending_unresolved, 'text-[#4B5563]'],
            ['Shop orders failed', data?.reconciliation_7d?.shop_orders_failed, 'text-red-700'],
            ['Shop orders refunded', data?.reconciliation_7d?.shop_orders_refunded, 'text-amber-700'],
            ['Shop orders pending (unresolved)', data?.reconciliation_7d?.shop_orders_pending_unresolved, 'text-[#4B5563]'],
          ].map(([label, val, color]) => (
            <div key={label} className="border border-[#E5E7EB] rounded p-3">
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-bold">{label}</p>
              <p className={`text-2xl font-bold mt-0.5 ${color}`}>{val ?? 0}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-4" data-testid="webhook-expected-types">
        <h4 className="text-xs font-bold uppercase tracking-wide text-[#4B5563] mb-2 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Backend listens for these events</h4>
        <ul className="text-xs font-mono space-y-0.5 text-[#0A0A0A]">
          {(data?.expected_event_types || []).map(t => <li key={t}>• {t}</li>)}
        </ul>
      </div>
    </div>
  );
};

export default AdminWebhookHealthTab;
